import React from "react";
import { Box, FormControl, RadioGroup, Button } from "@mui/material";
import { FormattedMessage } from "react-intl";
import ChoiceItem from "../ChoiceItem";
import { SegmentLinkSelect, segmentChoices } from "../../Project/SegmentLink";

// `segments` — the linked project's segments. Linked to one (`question.segment`),
// the choices are the segment's and are shown locked: the BE owns them.
function SingleChoiceQuestion({
  question,
  index,
  handleChoiceChange,
  handleQuestionChange,
  handleAddChoice,
  enqueueSnackbar,
  segments = [],
  usedSegmentIds = [],
  segmentLocked = false,
}) {
  const linked = Boolean(question.segment);

  // Linking takes the segment's choices; unlinking keeps them, as ordinary ones.
  const handleLinkSegment = (segment) => {
    handleQuestionChange(index, "segment", segment ? segment.id : null);
    if (segment) {
      handleChoiceChange(index, null, "choices", segmentChoices(segment).map((c) => ({ ...c, tags: [] })));
    } else {
      handleChoiceChange(index, null, "choices", question.choices.map(({ segmentChoice, ...c }) => c));
    }
  };

  const handleRemoveChoice = (questionIndex, choiceIndex) => {
    // Prevent removing if there are 2 or fewer choices (validation requires at least 2)
    if (question.choices.length <= 2) {
      enqueueSnackbar("Single-choice questions must have at least two choices.", {
        variant: "warning",
        autoHideDuration: 2000,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      return;
    }

    const updatedChoices = question.choices.filter((_, idx) => idx !== choiceIndex);
    handleChoiceChange(questionIndex, null, "choices", updatedChoices);
  };

  return (
    <>
      <SegmentLinkSelect
        id={`question-${index}-segment`}
        segments={segments}
        value={question.segment}
        onChange={handleLinkSegment}
        usedSegmentIds={usedSegmentIds}
        disabled={segmentLocked}
        sx={{ mb: 1.5 }}
      />
      <FormControl sx={{ width: "100%" }} component="fieldset">
        <RadioGroup>
          {question.choices.map((choice, choiceIndex) => (
            <ChoiceItem
              key={choiceIndex}
              choice={choice}
              choiceIndex={choiceIndex}
              questionIndex={index}
              handleChoiceChange={handleChoiceChange}
              handleRemoveChoice={handleRemoveChoice}
              isMultiChoice={false}
              locked={linked}
            />
          ))}
        </RadioGroup>
      </FormControl>
      {!linked && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            onClick={() => handleAddChoice(index)}
            variant="outlined"
            size="small"
            sx={{
              textTransform: "none",
              color: "#1976d2",
              borderColor: "#1976d2",
              borderRadius: "4px",
              fontSize: "0.875rem",
              fontWeight: "medium",
              px: 2,
              py: 0.5,
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                borderColor: "#1976d2",
              },
            }}
          >
            <FormattedMessage id="survey.single_choice.add_choice_button" />
          </Button>
        </Box>
      )}
    </>
  );
}

export default SingleChoiceQuestion;
